'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { HeaderSection } from '@/components/shared/header-section'
import { PageContainer } from '@/components/shared/page-container'
import { gql, useLazyQuery, useQuery } from '@apollo/client'
import { Button } from '@/components/ui/button'
import { PlusIcon } from '@radix-ui/react-icons'
import ProductCreationWizard from './components/ProductCreationWizard'
import { DataTableManual } from '../../components/shared/data-table-manual/data-table-manual'
import { columns } from './components/columns'
import { GET_TAGS_FOR_SELECT } from '../Tags/tagsQueries'
import { GET_CATEGORIES_FOR_SELECT } from '../Categories/categoriesQueries'
import { DataTableToolbar } from '../../components/shared/data-table-manual/data-table-toolbar-manual'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import ProductForm from './components/ProductForm'
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation'
import { DELETE_BULK_PRODUCTS, DELETE_MANY_PRODUCTS } from './productQueries'
import { PageContent } from '@/components/shared/page-content'
import { getOrderSortParams } from '@/utils/orderUtils'
import { useSearchParams } from 'react-router-dom'
import { useLanguage } from '@/context/LanguageContext'

export const GET_PRODUCTS = gql`
  query GET_PRODUCTS(
    $language: Language
    $orderBy: [ProductContainerOrderByWithRelationInput!]
    $searchTerm: String
    $take: Int
    $skip: Int
    $filters: [ProductContainerWhereInput]
  ) {
    findManyProductContainerCount(
      orderBy: $orderBy
      where: {
        OR: [
          {
            single_product: {
              is: {
                OR: [
                  { sku: { mode: insensitive, contains: $searchTerm } }
                  {
                    product_profiles: {
                      some: {
                        name: { mode: insensitive, contains: $searchTerm }
                        language: { equals: $language }
                      }
                    }
                  }
                ]
              }
            }
          }
          {
            advanced_product: {
              is: {
                OR: [
                  {
                    advanced_product_profiles: {
                      some: {
                        name: { mode: insensitive, contains: $searchTerm }
                        language: { equals: $language }
                      }
                    }
                  }
                ]
              }
            }
          }
        ]
        AND: $filters
      }
    )
    findManyProductContainer(
      orderBy: $orderBy
      where: {
        OR: [
          {
            single_product: {
              is: {
                OR: [
                  { sku: { mode: insensitive, contains: $searchTerm } }
                  {
                    product_profiles: {
                      some: {
                        name: { mode: insensitive, contains: $searchTerm }
                        language: { equals: $language }
                      }
                    }
                  }
                ]
              }
            }
          }
          {
            advanced_product: {
              is: {
                OR: [
                  {
                    advanced_product_profiles: {
                      some: {
                        name: { mode: insensitive, contains: $searchTerm }
                        language: { equals: $language }
                      }
                    }
                  }
                ]
              }
            }
          }
        ]
        AND: $filters
      }
      take: $take
      skip: $skip
    ) {
      id
      visible
      categories {
        id
        category_profiles(where: { language: { equals: $language } }) {
          name
        }
      }
      type
      advanced_product {
        advanced_product_profiles(where: { language: { equals: $language } }) {
          name
          description
          language
        }
        images {
          display_order
          id
          src_thumbnail
        }
      }
      single_product {
        product_profiles(where: { language: { equals: $language } }) {
          name
          description
        }
      }
    }
  }
`

/**
 * Build URLSearchParams from current state.
 * Single source of truth for URL param format.
 */
function buildSearchParams(
  filters: Map<string, Set<any>>,
  searchTerm: string,
  pageIndex: number
): URLSearchParams {
  const params = new URLSearchParams()

  // Handle tags
  const tagsFilter = filters.get('tags.id')
  if (tagsFilter && tagsFilter.size > 0) {
    params.set('tags', Array.from(tagsFilter).join(','))
  }

  // Handle categories
  const categoriesFilter = filters.get('categories.id')
  if (categoriesFilter && categoriesFilter.size > 0) {
    params.set('categories', Array.from(categoriesFilter).join(','))
  }

  // Handle visibility
  const visibilityFilter = filters.get('visible')
  if (visibilityFilter && visibilityFilter.size === 1) {
    params.set('visible', String(Array.from(visibilityFilter)[0]))
  }

  // Handle search term
  if (searchTerm) {
    params.set('search', searchTerm)
  }

  // Only set page when > 0 (page 1 = no param)
  if (pageIndex > 0) {
    params.set('page', String(pageIndex + 1))
  }

  return params
}

export default function ProductCatalog() {
  const { data: tagsData } = useQuery(GET_TAGS_FOR_SELECT)
  const { data: categoriesData } = useQuery(GET_CATEGORIES_FOR_SELECT)
  const openProductForm = useOpenDrawer(ProductForm)
  const [searchParams, setSearchParams] = useSearchParams()
  const setSearchParamsRef = useRef(setSearchParams)
  setSearchParamsRef.current = setSearchParams
  const { languageId, language } = useLanguage()
  const [searchTerm, setSearchTerm] = useState(() => {
    // Initialize searchTerm from URL on first render
    const searchParam = new URLSearchParams(window.location.search).get(
      'search'
    )
    return searchParam || ''
  })
  const [tableData, setTableData] = useState([])
  const [tableDataCount, setTableDataCount] = useState(0)
  const [order, setOrder] = useState([])
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const [filters, setFilters] = React.useState<Map<string, Set<any>>>(new Map())

  const [pagination, setPagination] = React.useState(() => {
    const pageParam = new URLSearchParams(window.location.search).get('page')
    const pageIndex = pageParam ? Math.max(0, Number(pageParam) - 1) : 0
    return { pageIndex: Number.isNaN(pageIndex) ? 0 : pageIndex, pageSize: 20 }
  })

  const [loadProducts, { data, error, loading }] = useLazyQuery(GET_PRODUCTS, {
    variables: {},
    fetchPolicy: 'cache-and-network',
  })

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_BULK_PRODUCTS,
    refetchQueries: ['GET_PRODUCTS'],
    messages: {
      success: 'Products deleted successfully',
      error: 'Failed to delete products',
    },
    itemName: 'product',
  })

  // Parse URL query parameters and set filters
  const parseFiltersFromURL = () => {
    const newFilters = new Map<string, Set<any>>()

    // Parse tags parameter
    const tagsParam = searchParams.get('tags')
    if (tagsParam) {
      const tagIds = tagsParam
        .split(',')
        .map(id => id.trim())
        .filter(id => id && !isNaN(Number(id)))
      if (tagIds.length > 0) {
        newFilters.set('tags.id', new Set(tagIds))
      }
    }

    // Parse categories parameter
    const categoriesParam = searchParams.get('categories')
    if (categoriesParam) {
      const categoryIds = categoriesParam
        .split(',')
        .map(id => id.trim())
        .filter(id => id && !isNaN(Number(id)))
      if (categoryIds.length > 0) {
        newFilters.set('categories.id', new Set(categoryIds))
      }
    }

    // Parse visibility parameter
    const visibleParam = searchParams.get('visible')
    if (visibleParam) {
      newFilters.set('visible', new Set([visibleParam]))
    }

    return newFilters
  }

  // Sync filters/search/page from URL (on mount and on back/forward navigation)
  useEffect(() => {
    const urlFilters = parseFiltersFromURL()

    // Only update filters if content actually changed (avoids unnecessary re-renders)
    const filtersChanged = (() => {
      if (urlFilters.size !== filters.size) return true
      for (const [key, urlSet] of urlFilters) {
        const currentSet = filters.get(key)
        if (!currentSet || currentSet.size !== urlSet.size) return true
        for (const val of urlSet) {
          if (!currentSet.has(val)) return true
        }
      }
      return false
    })()
    if (filtersChanged) {
      setFilters(urlFilters)
    }

    // Parse search term (normalize null to '' to avoid null !== '' always being true)
    const searchParam = searchParams.get('search') || ''
    if (searchParam !== searchTerm) {
      setSearchTerm(searchParam)
    }

    // Parse page param (1-indexed in URL -> 0-indexed internally)
    const pageParam = searchParams.get('page')
    const urlPageIndex = pageParam ? Math.max(0, Number(pageParam) - 1) : 0
    const safePageIndex = Number.isNaN(urlPageIndex) ? 0 : urlPageIndex
    if (safePageIndex !== pagination.pageIndex) {
      setPagination(prev => ({ ...prev, pageIndex: safePageIndex }))
    }
  }, [searchParams.toString()])

  // Handle pagination changes from table controls — pushes URL history entry
  const handlePaginationChange = React.useCallback(
    (updaterOrValue: React.SetStateAction<{ pageIndex: number; pageSize: number }>) => {
      const next =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(pagination)
          : updaterOrValue

      setPagination(next)

      if (next.pageIndex !== pagination.pageIndex) {
        const newSearchParams = new URLSearchParams(searchParams)
        if (next.pageIndex > 0) {
          newSearchParams.set('page', String(next.pageIndex + 1))
        } else {
          newSearchParams.delete('page')
        }
        setSearchParamsRef.current(newSearchParams)
      }
    },
    [pagination, searchParams]
  )

  // URL-aware search handler: updates state + URL directly (replaces history entry)
  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchTerm(term)
      setPagination(prev => ({ ...prev, pageIndex: 0 }))
      setSearchParamsRef.current(buildSearchParams(filters, term, 0), { replace: true })
    },
    [filters]
  )

  // Sort change handler: resets pagination to page 0 so the user doesn't stay
  // on a stale page when changing sort order.
  const handleSortingChange = useCallback(
    (updaterOrValue: React.SetStateAction<any>) => {
      setOrder(updaterOrValue)
      setPagination(prev => ({ ...prev, pageIndex: 0 }))
    },
    []
  )

  // URL-aware filter handler: updates state + URL directly (replaces history entry)
  const handleFilterChange = useCallback(
    (newFilters: Map<string, Set<any>>) => {
      setFilters(newFilters)
      setPagination(prev => ({ ...prev, pageIndex: 0 }))
      setSearchParamsRef.current(buildSearchParams(newFilters, searchTerm, 0), {
        replace: true,
      })
    },
    [searchTerm]
  )

  useEffect(() => {
    const queryFilter = Array.from(filters.entries())
      .map(([key, value]) => {
        if (!value.size) return null
        if (key === 'tags.id') {
          return {
            tags: {
              some: {
                id: {
                  in: Array.from(value).map(id => Number(id)),
                },
              },
            },
          }
        }
        if (key === 'categories.id') {
          return {
            categories: {
              some: {
                id: {
                  in: Array.from(value).map(id => Number(id)),
                },
              },
            },
          }
        }
        if (key === 'visible') {
          // Handle visibility filter separately since it's a boolean
          const filtervalues = Array.from(value)
          if (filtervalues.length === 2) {
            return null
          } else {
            const visibilityvalue = filtervalues[0] === 'true'
            return {
              visible: {
                equals: visibilityvalue,
              },
            }
          }
        }
        return {
          [key]: {
            in: Array.from(value),
          },
        }
      })
      .filter(Boolean)

    loadProducts({
      variables: {
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        filters: queryFilter,
        orderBy: getOrderSortParams(order),
        language: language,
      },
    })
  }, [searchTerm, pagination, order, filters, language])

  useEffect(() => {
    if (data) {
      const dataParsed = data.findManyProductContainer.map((item: any) => {
        return {
          id: item.id,
          type: item.type,
          image: item.advanced_product?.images?.find(
            (image: any) => image.display_order === 0
          )?.src_thumbnail,
          name: item.single_product
            ? item.single_product?.product_profiles[0]?.name
            : item.advanced_product?.advanced_product_profiles[0]?.name,
          visible: item.visible !== undefined ? item.visible : null,
          categories:
            item.categories?.map((category: any) => ({
              id: category.id,
              name: category.category_profiles[0]?.name,
            })) || [],
          onEdit: openProductForm,
        }
      })

      setTableData(dataParsed)
      setTableDataCount(data.findManyProductContainerCount ?? 0)
    }
  }, [data])

  const heading = 'Products'

  // Handlers for the product creation wizard
  const openWizard = () => setIsWizardOpen(true)
  const closeWizard = () => setIsWizardOpen(false)

  // Handler for product creation success
  const handleProductCreated = () => {
    loadProducts({
      variables: {
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        filters: Array.from(filters.entries())
          .map(([key, value]) => {
            if (!value.size) return null
            if (key === 'tags.id') {
              return {
                tags: {
                  some: {
                    id: {
                      in: Array.from(value).map(id => Number(id)),
                    },
                  },
                },
              }
            }
            if (key === 'categories.id') {
              return {
                categories: {
                  some: {
                    id: {
                      in: Array.from(value).map(id => Number(id)),
                    },
                  },
                },
              }
            }
            if (key === 'visible') {
              // Handle visibility filter separately since it's a boolean
              const filtervalues = Array.from(value)
              if (filtervalues.length === 2) {
                return null
              } else {
                const visibilityvalue = filtervalues[0] === 'true'
                return {
                  visible: {
                    equals: visibilityvalue,
                  },
                }
              }
            }
            return {
              [key]: {
                in: Array.from(value),
              },
            }
          })
          .filter(Boolean),
        orderBy: getOrderSortParams(order),
        language: language,
      },
    })
  }

  return (
    <PageContent>
      <div className="flex justify-between items-center">
        <HeaderSection title={heading} />
        <Button onClick={openWizard} className="ml-auto">
          <PlusIcon className="mr-2 h-4 w-4" /> Create Product
        </Button>
      </div>

      {/* Product Creation Wizard */}
      <ProductCreationWizard
        isOpen={isWizardOpen}
        onClose={closeWizard}
        onSuccess={handleProductCreated}
      />
      <DataTableManual
        data={tableData}
        columns={columns}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={handlePaginationChange}
        onSortingChange={handleSortingChange}
        onColumnFiltersChange={handleFilterChange}
        isLoading={loading}
        toolbar={(table: any) => {
          // Convert numeric tag IDs to strings for the options
          const tagOptions =
            tagsData?.findManyTag?.map((tag: any) => ({
              label: tag.tag_profiles[0]?.name,
              value: String(tag.id),
            })) || []

          // Convert numeric category IDs to strings for the options
          const categoryOptions =
            categoriesData?.findManyCategory?.map((category: any) => ({
              label: category.category_profiles[0]?.name,
              value: String(category.id),
            })) || []

          // Create visibility filter options
          const visibilityOptions = [
            { label: 'Visible', value: 'true' },
            { label: 'Not Visible', value: 'false' },
          ]

          return (
            <DataTableToolbar
              table={table}
              config={{
                onDelete: onDelete,
                search: {
                  placeholder: 'Filter products...',
                  accessor: 'name',
                  onSearch: handleSearchChange,
                  onFilter: handleFilterChange,
                  initialValue: searchTerm,
                },
                columns: [
                  {
                    title: 'Tags',
                    accessor: 'tags.id',
                    options: tagOptions,
                  },
                  {
                    title: 'Categories',
                    accessor: 'categories.id',
                    options: categoryOptions,
                  },
                  {
                    title: 'Visibility',
                    accessor: 'visible',
                    options: visibilityOptions,
                  },
                ],
                initialFilters: filters,
              }}
            />
          )
        }}
      />
    </PageContent>
  )
}
